import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import dayjs from "dayjs";
import axios from "axios";
import { useApi } from "../../../../../hooks/useApi";
import DefaultModalLayout from "../../../../Modal/DefaultModalLayout";
import CalendarLineBtn from "../../../../Button/CalendarLineBtn";
import CalendarModal from "../../../../Modal/CalendarModal";
import { ModalLabel } from "../../../Users/UserDetail/CommentModal/TargetContainer";

interface EndDateSetModalProps {
  cancelEvent: () => void;
  partyId: string;
  enddate: string;
  updateEvent: () => Promise<void>;
}

export default function EndDateSetModal({
  cancelEvent,
  partyId,
  enddate,
  updateEvent,
}: EndDateSetModalProps) {
  const [submitClick, setSubmitClick] = useState(false);
  const [selectedDate, setSelectedDate] = useState("선택");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [passStartDate, setPassStartDate] = useState<string | null>(null);
  const [infoErrorMsg, setInfoErrorMsg] = useState<string | null>(null);
  const [infoLoaded, setInfoLoaded] = useState(false);

  const currentEndDate = dayjs(enddate).add(-1, "day").format("YYYY-MM-DD");

  // 모달 열릴 때 선택 가능 시작일 조회
  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await axios.post(
          `/api/party/${partyId}/chgPartyEnddateInfo`,
        );
        const resData = res?.data;
        if (resData?.success === false) {
          setInfoErrorMsg(resData?.data ?? "요청을 처리할 수 없습니다.");
        } else {
          setPassStartDate(resData?.pass_startDate ?? null);
        }
      } catch (err: any) {
        const errData = err?.response?.data;
        setInfoErrorMsg(
          errData?.data ?? errData?.message ?? "요청을 처리할 수 없습니다.",
        );
      } finally {
        setInfoLoaded(true);
      }
    };
    fetchInfo();
  }, [partyId]);

  const { response, error } = useApi({
    method: "post",
    url: submitClick ? `party/${partyId}/chenddate` : undefined,
    data: {
      enddate: selectedDate,
    },
  });

  useEffect(() => {
    if (response?.status === 200) {
      cancelEvent();
      updateEvent();
      toast.success("파티 종료일이 변경되었습니다.", {});
    }
  }, [response]);

  useEffect(() => {
    if (error) {
      setSubmitClick(false);
      toast.error(error?.data ?? error?.message ?? "오류가 발생했습니다.", {});
    }
  }, [error]);

  if (infoErrorMsg) {
    return (
      <DefaultModalLayout
        modalTitle="파티 종료일 변경"
        isSmallModal={true}
        closeEvent={() => cancelEvent()}
        confirmEvent={() => cancelEvent()}
        type="view"
        confirmBtnText="닫기"
      >
        <ErrorMessage>{infoErrorMsg}</ErrorMessage>
      </DefaultModalLayout>
    );
  }

  return (
    <DefaultModalLayout
      modalTitle="파티 종료일 변경"
      isSmallModal={true}
      isOverflowVisible={true}
      closeEvent={() => cancelEvent()}
      confirmEvent={() => setSubmitClick(true)}
      type="input"
      confirmDisabled={selectedDate === "선택"}
    >
      <>
        <Row>
          <ModalLabel>현재 종료일</ModalLabel>
          <ValueText>{currentEndDate}</ValueText>
        </Row>

        <Row>
          <ModalLabel>변경 종료일</ModalLabel>
          <CalendarContainer>
            <CalendarLineBtn
              onClickEvent={() => {
                if (infoLoaded && passStartDate) {
                  setCalendarOpen(true);
                }
              }}
              value={selectedDate}
              initValue={"선택"}
              disabled={!infoLoaded || !passStartDate}
            />

            {calendarOpen && passStartDate && (
              <CalendarModal
                closeEvent={() => setCalendarOpen(false)}
                changeEvent={(date: string) => setSelectedDate(date)}
                dateValue={currentEndDate}
                minDate={passStartDate}
                maxDate={currentEndDate}
              />
            )}
          </CalendarContainer>
        </Row>
      </>
    </DefaultModalLayout>
  );
}

const Row = styled.div`
  width: 100%;
  display: flex;
  align-items: center;

  &:not(:last-child) {
    margin-bottom: 20px;
  }
`;

const ValueText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1e1e1e;
`;

const CalendarContainer = styled.div`
  position: relative;
`;

const ErrorMessage = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #e74c3c;
  text-align: center;
  padding: 20px 0;
  line-height: 22px;
`;
